/**
 * Shared display constants for Live Trade Lifecycle Management (Phase 6).
 * Mirrors backend `PositionStatus`/`ReconciliationStatus`/
 * `StrategyPositionRead.status`. Reuses Phase 4's own `ORDER_STATE_LABELS`/
 * `ORDER_STATE_COLORS`/`STRATEGY_STATUS_LABELS`/`STRATEGY_STATUS_COLORS`
 * (`@/pages/execution/constants`) for `BrokerOrderState`/
 * `StrategyExecutionStatus` rather than redefining them here.
 *
 * Spec Part 3: "Use clear status indicators... Do not rely on color
 * alone." — `ORDER_STATE_ICONS` below pairs a plain-text glyph with every
 * `BrokerOrderState`, always rendered together with (never instead of) the
 * existing color-coded `Chip`.
 */
import type { BrokerOrderState, PositionStatus, ReconciliationStatus } from "@/types";

export const ORDER_STATE_ICONS: Record<BrokerOrderState, string> = {
  PENDING: "…",
  OPEN: "●",
  PARTIALLY_FILLED: "◐",
  FILLED: "✓",
  REJECTED: "✕",
  CANCELLED: "⊘",
  EXPIRED: "⊘",
  UNKNOWN: "?",
};

export const POSITION_STATUS_LABELS: Record<PositionStatus, string> = {
  OPEN: "Open",
  PARTIALLY_CLOSED: "Partially Closed",
  CLOSED: "Closed",
};

export const POSITION_STATUS_COLORS: Record<PositionStatus, "success" | "warning" | "default"> = {
  OPEN: "success",
  PARTIALLY_CLOSED: "warning",
  CLOSED: "default",
};

export const RECONCILIATION_LABELS: Record<ReconciliationStatus, string> = {
  MATCHED: "Matched",
  MISMATCH: "⚠ Mismatch",
  UNVERIFIED: "Unverified",
};

export const RECONCILIATION_COLORS: Record<ReconciliationStatus, "success" | "error" | "default"> = {
  MATCHED: "success",
  MISMATCH: "error",
  UNVERIFIED: "default",
};

export const STRATEGY_POSITION_STATUS_COLORS: Record<string, "success" | "warning" | "error" | "default"> = {
  ACTIVE: "success",
  PENDING: "default",
  CLOSED: "default",
  REQUIRES_ATTENTION: "error",
};
